import React, { useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import ReCAPTCHA from 'react-google-recaptcha';

function RequestQuoteModal({ isOpen, onClose, formData, handleInputChange, handleFormSubmit, productName }) {
    if (!isOpen || typeof window === 'undefined' || !document.body) return null;
    
    // Same reCAPTCHA site key as used in EnquiryCartForm
    const RECAPTCHA_SITE_KEY = "6Lf3p0QrAAAAAHGcVJUBl0z999DFNxzUopth8mbS";
    
    const recaptchaRef = useRef(null);
    const modalRef = useRef(null);
    const [captchaError, setCaptchaError] = useState("");
    
    // Handle click outside to close modal
    const handleBackdropClick = (e) => {
        if (modalRef.current && !modalRef.current.contains(e.target)) {
            onClose();
        }
    };
    
    const onSubmitWithRecaptcha = (e) => {
        e.preventDefault();
        
        // Get the recaptcha token
        const recaptchaValue = recaptchaRef.current.getValue();
        
        if (!recaptchaValue) {
            setCaptchaError("Please complete the reCAPTCHA verification");
            return;
        }
        
        setCaptchaError("");
        // Pass the form submission to the parent component
        handleFormSubmit(e);
    };
    
    // Add CSS to fix reCAPTCHA iframe z-index
    useEffect(() => {
        if (isOpen) {
            // Add a style tag to ensure reCAPTCHA iframe has high z-index
            const styleTag = document.createElement('style');
            styleTag.innerHTML = `
                .grecaptcha-badge, 
                iframe[src*="recaptcha"], 
                .g-recaptcha iframe,
                #recaptcha-container iframe {
                    z-index: 10000000 !important;
                }
            `;
            document.head.appendChild(styleTag);
            
            return () => {
                document.head.removeChild(styleTag);
            };
        }
    }, [isOpen]);
    
    return createPortal(
        <div className="fixed inset-0 z-[9999999]" style={{ zIndex: 9999999 }}>
            {/* Backdrop */}
            <div 
                className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center p-4" 
                onClick={handleBackdropClick}
            >
                {/* Modal content */}
                <div 
                    ref={modalRef} 
                    className="bg-white rounded-lg shadow-lg w-full max-w-md p-6 relative max-h-[80vh] overflow-y-auto my-auto"
                >
                    <button
                        className="absolute top-3 right-3 text-gray-400 hover:text-gray-700 text-2xl"
                        onClick={onClose}
                        aria-label="Close modal"
                    >
                        &times;
                    </button>
                    <h3 className="text-xl font-bold mb-4 text-center">Request A Quote - {productName || 'Traditional Nail & Tool Bag'}</h3>
                    <form onSubmit={onSubmitWithRecaptcha} className="space-y-4">
                        {/* Two-column grid for desktop, single column for mobile */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Name field */}
                            <div>
                                <label className="block mb-1 font-medium" htmlFor="sample-name">Name*</label>
                                <input
                                    id="sample-name"
                                    name="name"
                                    type="text"
                                    placeholder="Name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    required
                                    className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                                />
                            </div>
                            
                            {/* Quantity field */}
                            <div>
                                <label className="block mb-1 font-medium" htmlFor="sample-quantity">Quantity*</label>
                                <div className="flex items-center border border-gray-300 rounded">
                                    <button 
                                        type="button"
                                        onClick={() => {
                                            const newQuantity = Math.max(1, parseInt(formData.quantity) - 1);
                                            handleInputChange({
                                                target: { name: 'quantity', value: newQuantity, type: 'number' }
                                            });
                                        }}
                                        className="px-3 py-2 text-gray-500 hover:bg-gray-100"
                                    >
                                        −
                                    </button>
                                    <input
                                        id="sample-quantity"
                                        name="quantity"
                                        type="number"
                                        min="1"
                                        value={formData.quantity}
                                        onChange={handleInputChange}
                                        required
                                        className="w-full text-center border-none focus:outline-none focus:ring-0"
                                    />
                                    <button 
                                        type="button"
                                        onClick={() => {
                                            const newQuantity = parseInt(formData.quantity) + 1;
                                            handleInputChange({
                                                target: { name: 'quantity', value: newQuantity, type: 'number' }
                                            });
                                        }}
                                        className="px-3 py-2 text-gray-500 hover:bg-gray-100"
                                    >
                                        +
                                    </button>
                                </div>
                            </div>
                            
                            {/* Phone field */}
                            <div>
                                <label className="block mb-1 font-medium" htmlFor="sample-phone">Phone No.*</label>
                                <input
                                    id="sample-phone"
                                    name="phone"
                                    type="tel"
                                    placeholder="Phone No."
                                    value={formData.phone}
                                    onChange={handleInputChange}
                                    required
                                    className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                                />
                            </div>
                            
                            {/* Email field */}
                            <div>
                                <label className="block mb-1 font-medium" htmlFor="sample-email">Email*</label>
                                <input
                                    id="sample-email"
                                    name="email"
                                    type="email"
                                    placeholder="Email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    required
                                    className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                                />
                            </div>
                            
                            {/* Address field */}
                            <div>
                                <label className="block mb-1 font-medium" htmlFor="sample-address">Address</label>
                                <input
                                    id="sample-address"
                                    name="address"
                                    type="text"
                                    placeholder="Address"
                                    value={formData.address}
                                    onChange={handleInputChange}
                                    className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                                />
                            </div>
                            
                            {/* Country field */}
                            <div>
                                <label className="block mb-1 font-medium" htmlFor="sample-country">Country*</label>
                                <input
                                    id="sample-country"
                                    name="country"
                                    type="text"
                                    placeholder="Country"
                                    value={formData.country}
                                    onChange={handleInputChange}
                                    required
                                    className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                                />
                            </div>
                        </div>
                        
                        {/* Message field - full width */}
                        <div>
                            <label className="block mb-1 font-medium" htmlFor="sample-message">Message</label>
                            <textarea
                                id="sample-message"
                                name="message"
                                placeholder="Message"
                                value={formData.message}
                                onChange={handleInputChange}
                                rows={3}
                                className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                            />
                        </div>
                        
                        {/* reCAPTCHA */}
                        <div className="flex justify-center my-4" id="recaptcha-container">
                            <div className="g-recaptcha">
                                <ReCAPTCHA
                                    ref={recaptchaRef}
                                    sitekey={RECAPTCHA_SITE_KEY}
                                    onChange={() => setCaptchaError("")}
                                />
                            </div>
                        </div>
                        {captchaError && (
                            <div className="text-red-600 text-sm text-center mt-2">{captchaError}</div>
                        )}
                        
                        <div className="flex justify-end gap-2">
                            <button
                                type="button"
                                className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                                onClick={onClose}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
                            >
                                Submit
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>,
        document.body
    );
}

export default RequestQuoteModal;
